/**
 * @stylon-content-sync
 * This component renders feature landing pages for stylon.co/features/*
 *
 * Content source: src/i18n/locales/{en,ar,he}.json → features.[featureKey].*
 *
 * When you modify behavior in the salon portal, update the matching
 * translation keys so the landing page and guide stay accurate:
 *
 *   FAQ keys:    faq1Q/A, faq2Q/A, faq3Q/A
 *   Guide steps: g1t/g1b … g6t/g6b  (title + body per step)
 *   Benefits:    b1 … b6
 *   How it works: step1, step2, step3
 *
 * Affected system pages by featureKey:
 *   booking       → stylon-salon-portal/src/Pages/Settings/index.js (Booking Settings)
 *   appointments  → stylon-salon-portal/src/Pages/Appointments/index.js
 *   crm           → stylon-salon-portal/src/Pages/Customers/
 *   notifications → stylon-salon-portal/src/Pages/Settings/index.js (Notifications)
 *   discounts     → stylon-salon-portal/src/Pages/Campaigns/
 *   gallery       → stylon-salon-portal/src/Pages/Gallery/
 *   reminders     → stylon-salon-portal/src/Pages/Settings/index.js (Reminders)
 *   team          → stylon-salon-portal/src/Pages/Specialists/
 *   payments      → stylon-salon-portal/src/Pages/Insights/
 *   website       → stylon-salon-portal/src/Pages/Profile/
 *   zoom          → stylon-salon-portal/src/Pages/Settings/index.js (Integrations)
 *   profile       → stylon-salon-portal/src/Pages/Profile/
 *   services      → stylon-salon-portal/src/Pages/Services/
 */
import React, { useState, useEffect } from 'react';
import { getOnboardingUrl } from "../../utils/portalUrl";
import { trackViewFeatures } from "../../utils/track";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaRocket, FaCheckCircle, FaCalendarCheck, FaUsers, FaGlobe, FaVideo,
  FaBell, FaUserFriends, FaCreditCard, FaCalendarAlt, FaBullhorn,
  FaTag, FaImages, FaIdCard, FaClipboardList, FaUserShield, FaQrcode,
  FaMobileAlt, FaChevronDown, FaChevronUp,
  FaMoneyBillWave, FaStar, FaBoxOpen, FaBullseye,
  FaTooth, FaCrosshairs, FaSyringe, FaFolderOpen, FaProjectDiagram,
  FaFileSignature, FaGraduationCap, FaStore, FaCashRegister, FaRobot,
  FaHotel, FaDoorOpen, FaUsersCog, FaCommentDots
} from 'react-icons/fa';
import { FEATURE_SHOTS } from './featureShots';
import { FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import Navbar from '../../Components/Navbar/Navbar';
import Footer from '../../Components/Footer/Footer';
import SEO from '../../Components/SEO/SEO';
import { FEATURE_META } from '../../seo/pageMeta';
import { RICH_FEATURES } from './richFeatures';
import { DEMOS } from '../../Components/Demos';
import DeepDive from '../../Components/DeepDive';
import ShotStrip, { shotImageJsonLd } from '../../Components/ShotStrip';
import SpecTable from '../../Components/SpecTable';
import RelatedLinks from '../../Components/RelatedLinks';
import styles from './FeaturePage.module.css';

const ICONS = {
  booking:       { Icon: FaCalendarCheck, color: '#FE4E00' },
  crm:           { Icon: FaUsers,         color: '#203052' },
  website:       { Icon: FaGlobe,         color: '#0ea5e9' },
  zoom:          { Icon: FaVideo,         color: '#2d8cff' },
  reminders:     { Icon: FaBell,          color: '#f59e0b' },
  team:          { Icon: FaUserFriends,   color: '#10b981' },
  payments:      { Icon: FaCreditCard,    color: '#8b5cf6' },
  appointments:  { Icon: FaCalendarAlt,   color: '#FE4E00' },
  notifications: { Icon: FaBullhorn,      color: '#203052' },
  discounts:     { Icon: FaTag,           color: '#10b981' },
  gallery:       { Icon: FaImages,        color: '#0ea5e9' },
  profile:       { Icon: FaIdCard,        color: '#8b5cf6' },
  services:      { Icon: FaClipboardList, color: '#FE4E00' },
  calendar:      { Icon: FaCalendarAlt,  color: '#0ea5e9' },
  roles:         { Icon: FaUserShield,   color: '#10b981' },
  qr:            { Icon: FaQrcode,       color: '#203052' },
  app:           { Icon: FaMobileAlt,    color: '#8b5cf6' },
  finance:       { Icon: FaMoneyBillWave, color: '#10b981' },
  reviews:       { Icon: FaStar,          color: '#f59e0b' },
  products:      { Icon: FaBoxOpen,       color: '#FE4E00' },
  marketing:     { Icon: FaBullseye,      color: '#203052' },

  // Published by the 2026-09 expansion.
  'dental-charting': { Icon: FaTooth,          color: '#0ea5e9' },
  'treatment-areas': { Icon: FaCrosshairs,     color: '#10b981' },
  'injectables':     { Icon: FaSyringe,        color: '#8b5cf6' },
  'records':         { Icon: FaFolderOpen,     color: '#203052' },
  'treatment-plans': { Icon: FaProjectDiagram, color: '#0891b2' },
  'agreements':      { Icon: FaFileSignature,  color: '#059669' },
  'courses':         { Icon: FaGraduationCap,  color: '#7c3aed' },
  'shop':            { Icon: FaStore,          color: '#FE4E00' },
  'pos':             { Icon: FaCashRegister,   color: '#f59e0b' },
  'auto-ads':        { Icon: FaRobot,          color: '#203052' },
  'hospitality':     { Icon: FaHotel,          color: '#0ea5e9' },
  'rooms':           { Icon: FaDoorOpen,       color: '#16a34a' },
  'group-booking':   { Icon: FaUsersCog,       color: '#d946a8' },
  'messages':        { Icon: FaCommentDots,    color: '#25D366' },
};

// Guide steps per feature (max 6)
const GUIDE_STEP_COUNTS = {
  booking: 5, appointments: 9, crm: 5, notifications: 5,
  discounts: 5, gallery: 5, reminders: 5, team: 5,
  payments: 5, website: 5, zoom: 5, profile: 6, services: 7,
  calendar: 5, roles: 5, qr: 5, app: 5,
  'dental-charting': 5, 'treatment-areas': 5, injectables: 5, records: 5,
  'treatment-plans': 5, agreements: 5, courses: 5, shop: 5, pos: 5,
  'auto-ads': 5, hospitality: 5, rooms: 5, 'group-booking': 5, messages: 5,
};

const FeaturePage = ({ featureKey }) => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar' || i18n.language === 'he';
  const { Icon, color } = ICONS[featureKey] || { Icon: FaRocket, color: '#FE4E00' };
  const BackArrow = isRtl ? FiArrowRight : FiArrowLeft;

  /* The first answer is open on arrival. Every other answer and every guide
   * step is in the DOM too, collapsed by CSS — see the FAQ block below for
   * why the old `&&` renders were a structured-data policy breach. */
  const [faqOpen, setFaqOpen] = useState(1);
  const [guideOpen, setGuideOpen] = useState(false);

  // GTM engagement signal — which specific feature the user is researching.
  useEffect(() => { trackViewFeatures({ feature: featureKey }); }, [featureKey]);

  const stepCount = GUIDE_STEP_COUNTS[featureKey] || 5;

  // Build FAQ JSON-LD
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [1, 2, 3].map(n => ({
      '@type': 'Question',
      name: t(`features.${featureKey}.faq${n}Q`),
      acceptedAnswer: { '@type': 'Answer', text: t(`features.${featureKey}.faq${n}A`) }
    }))
  };

  // Build HowTo JSON-LD
  const howToJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: t(`features.${featureKey}.badge`) + ' — ' + t('features.common.guideTitle'),
    step: Array.from({ length: stepCount }, (_, i) => i + 1).map(n => ({
      '@type': 'HowToStep',
      name: t(`features.${featureKey}.g${n}t`),
      text: t(`features.${featureKey}.g${n}b`)
    }))
  };

  const pageMeta = FEATURE_META[featureKey] || {};

  // Absent for the 21 feature pages that shipped before the 2026-09 expansion,
  // which is exactly how they keep rendering as they always did.
  const rich = RICH_FEATURES[featureKey];
  const Demo = rich ? DEMOS[featureKey] : null;

  return (
    <div className={styles.page}>
      <SEO
        title={pageMeta.title || `${featureKey} | Stylon`}
        description={pageMeta.description || ''}
        canonical={pageMeta.canonical || `/features/${featureKey}`}
        jsonLd={[...(pageMeta.jsonLd || []), faqJsonLd, howToJsonLd, ...shotImageJsonLd(featureKey, i18n.language)]}
      />
      <Navbar />

      {/* Hero */}
      <section className={styles.hero}>
        <div className="container">
          <Link to="/features" className={styles.backLink}>
            <BackArrow /> {t('features.common.backToFeatures')}
          </Link>
          <div className={styles.heroBadge} style={{ backgroundColor: color + '22', color }}>
            <Icon className={styles.badgeIcon} />
            {t(`features.${featureKey}.badge`)}
          </div>
          <h1 data-aos="fade-up">{t(`features.${featureKey}.heroTitle`)}</h1>
          <p data-aos="fade-up" data-aos-delay="100">{t(`features.${featureKey}.heroSubtitle`)}</p>
          <a
            href={getOnboardingUrl("cta")}
            className={styles.heroCta}
            data-aos="fade-up"
            data-aos-delay="200"
          >
            {t('features.common.cta')}
          </a>
        </div>
      </section>

      {/* See it in the app — real screenshot (only for features that ship one) */}
      {FEATURE_SHOTS[featureKey] && (
        <section className={styles.shotSection}>
          <div className="container">
            <div
              className={styles.shotWrap}
              style={{ background: `radial-gradient(60% 70% at 50% 0%, ${color}1f, transparent 72%)` }}
              data-aos="fade-up"
            >
              <img src={FEATURE_SHOTS[featureKey]} alt="" className={styles.shotImg} loading="lazy" />
            </div>
          </div>
        </section>
      )}

      {/* Live demo — the product's own geometry, not a picture of it */}
      {Demo && (
        <section className={styles.demoSection}>
          <div className="container">
            <h2 data-aos="fade-up">{t('features.common.demoTitle', 'Try it right here')}</h2>
            <p className={styles.demoHint}>{t(`features.${featureKey}.demoHint`)}</p>
            <div data-aos="fade-up" data-aos-delay="80">
              <Demo color={color} />
            </div>
          </div>
        </section>
      )}

      {/* How it works */}
      <section className={styles.howItWorks}>
        <div className="container">
          <h2 data-aos="fade-up">{t('features.common.howItWorksTitle')}</h2>
          <div className={styles.stepsRow}>
            {[1, 2, 3].map((n) => (
              <div key={n} className={styles.stepCard} data-aos="fade-up" data-aos-delay={n * 80}>
                <div className={styles.stepNumber} style={{ backgroundColor: color + '18', color }}>
                  {n}
                </div>
                <p>{t(`features.${featureKey}.step${n}`)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className={styles.benefits}>
        <div className="container">
          <h2 data-aos="fade-up">{t('features.common.benefitsTitle')}</h2>
          <div className={styles.benefitsGrid}>
            {[1, 2, 3, 4, 5, 6].map((n) => (
              <div key={n} className={styles.benefitItem} data-aos="fade-up" data-aos-delay={n * 60}>
                <FaCheckCircle className={styles.checkIcon} style={{ color }} />
                <span>{t(`features.${featureKey}.b${n}`)}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {rich && <DeepDive featureKey={featureKey} count={rich.deepDive} color={color} />}
      <ShotStrip featureKey={featureKey} color={color} />
      {rich && <SpecTable featureKey={featureKey} rows={rich.spec} color={color} />}

      {/* FAQ */}
      <section className={styles.faqSection}>
        <div className="container">
          <h2 data-aos="fade-up">{t('features.common.faqTitle')}</h2>
          <div className={styles.faqList}>
            {[1, 2, 3].map((n) => (
              <div
                key={n}
                className={`${styles.faqItem} ${faqOpen === n ? styles.faqItemOpen : ''}`}
                onClick={() => setFaqOpen(faqOpen === n ? null : n)}
              >
                <div className={styles.faqQuestion} role="button" aria-expanded={faqOpen === n}>
                  <span>{t(`features.${featureKey}.faq${n}Q`)}</span>
                  {faqOpen === n
                    ? <FaChevronUp className={styles.faqChevron} style={{ color }} />
                    : <FaChevronDown className={styles.faqChevron} style={{ color }} />
                  }
                </div>
                {/* ALWAYS RENDERED, COLLAPSED BY CSS.
                  *
                  * This was `faqOpen === n && (…)`, which does not collapse an
                  * answer — it omits it. Measured on production
                  * /features/online-booking/: FAQPage marked up 3 questions
                  * and 3 answers, and 0 of the answers were anywhere in the
                  * page's text. Google's policy requires the Q&A that FAQPage
                  * marks up to be visible on the page, and 35 feature pages ×
                  * 3 languages shipped 70 blocks that were not. The copy was
                  * written and translated the whole time.
                  *
                  * An accordion is allowed to hide its answer behind a click;
                  * it is not allowed to not have one. No `aria-hidden` on the
                  * panel: what a reader can reach by clicking, a reader using
                  * assistive technology can reach too. */}
                <div
                  className={`${styles.faqAnswer} ${faqOpen === n ? styles.faqAnswerOpen : ''}`}
                  data-testid="faq-answer"
                  data-open={faqOpen === n ? 'true' : 'false'}
                >
                  <p>{t(`features.${featureKey}.faq${n}A`)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How to Use Guide (collapsible) */}
      <section className={styles.guideSection} id="guide">
        <div className="container">
          <div
            className={styles.guideHeader}
            onClick={() => setGuideOpen(v => !v)}
          >
            <div className={styles.guideTitleRow}>
              <div className={styles.guideIconBadge} style={{ backgroundColor: color + '18', color }}>
                <Icon />
              </div>
              <h2>{t('features.common.guideTitle')}: {t(`features.${featureKey}.badge`)}</h2>
            </div>
            <button className={styles.guideToggleBtn} style={{ color }} type="button" aria-expanded={guideOpen}>
              {guideOpen ? t('features.common.guideCollapse') : t('features.common.guideExpand')}
              {guideOpen ? <FaChevronUp className={styles.guideChevron} /> : <FaChevronDown className={styles.guideChevron} />}
            </button>
          </div>

          {/* Same reason as the FAQ answers above: `guideOpen && (…)` left the
            * HowTo block describing 5 steps that were nowhere in the page. */}
          {(
            <div
              className={`${styles.guideSteps} ${guideOpen ? styles.guideStepsOpen : ''}`}
              data-testid="guide-steps"
              data-open={guideOpen ? 'true' : 'false'}
            >
              {Array.from({ length: stepCount }, (_, i) => i + 1).map(n => (
                <div key={n} className={styles.guideStep} data-aos="fade-up" data-aos-delay={n * 50}>
                  <div className={styles.guideStepNum} style={{ backgroundColor: color + '18', color }}>{n}</div>
                  <div className={styles.guideStepContent}>
                    <strong>{t(`features.${featureKey}.g${n}t`)}</strong>
                    <p>{t(`features.${featureKey}.g${n}b`)}</p>
                  </div>
                </div>
              ))}
              <div className={styles.guideFooter}>
                <a
                  href={getOnboardingUrl("cta")}
                  className={styles.guideCtaLink}
                  style={{ backgroundColor: color }}
                >
                  {t('features.common.cta')}
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      <RelatedLinks scope="features" pageKey={featureKey} color={color} />

      {/* Final CTA */}
      <section className={styles.ctaSection}>
        <div className="container">
          <h2>{t('features.common.ctaTitle')}</h2>
          <p>{t('features.common.ctaSubtitle')}</p>
          <a
            href={getOnboardingUrl("cta")}
            className={styles.ctaButton}
          >
            {t('features.common.cta')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FeaturePage;
